import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Github, ExternalLink } from "lucide-react";

export default function Windows() {
  const [tab, setTab] = useState("sdk");

  return (
    <div>
      <section className="bg-[#f4f5f7] py-14 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <h1 className="text-4xl font-semibold text-slate-800">Windows</h1>
        </div>
      </section>

      <article className="max-w-5xl mx-auto px-6 lg:px-10 py-14">
        <Link
          to="/development-kits"
          className="inline-flex items-center gap-1 text-sm text-[#29abe2] hover:underline mb-8"
        >
          <ChevronLeft className="w-4 h-4" /> Back to Development Kits
        </Link>

        <div className="border-b border-slate-200 flex gap-1 flex-wrap">
          {[
            { id: "sdk", label: "SDK" },
            { id: "overview", label: "Overview" },
            { id: "docs", label: "Documentation" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-6 py-3 text-sm font-semibold uppercase tracking-wide transition-colors ${
                tab === t.id
                  ? "text-[#29abe2] border-b-2 border-[#29abe2] -mb-px"
                  : "text-slate-600 hover:text-slate-800"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="py-10">
          {tab === "sdk" && <SdkTab />}
          {tab === "overview" && <OverviewTab />}
          {tab === "docs" && <DocsTab />}
        </div>
      </article>
    </div>
  );
}

function GitHubBadge({ href }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold px-5 py-2.5 rounded transition-colors"
    >
      <Github className="w-4 h-4" />
      Get source on GitHub
    </a>
  );
}

function SdkTab() {
  return (
    <div className="space-y-10">
      <section>
        <h2 className="text-2xl md:text-3xl font-semibold text-slate-800 mb-4">TCMP SDK</h2>
        <p className="text-slate-700 leading-relaxed text-[16px]">
          The TCMP SDK natively supports TCMP TappyUSB devices without any special additional hardware. However,
          due to the spotty nature of native BLE support on the platform, interfacing to TappyBLE devices requires
          the use of a Bluegiga BLED112 BLE dongle. When used to connect to a TappyBLE device, the SDK will
          configure the Bluegiga dongle at runtime, so no special version is needed. For convenience, you can
          request a dongle to be included with your TappyBLE order. For additional information, please see the SDK
          repository on GitHub.
        </p>
        <div className="mt-5">
          <GitHubBadge href="https://github.com/TapTrack/Tcmp-CSharp-SDK" />
        </div>
      </section>

      <section>
        <h2 className="text-2xl md:text-3xl font-semibold text-slate-800 mb-4">TappyUSB Classic SDK</h2>
        <p className="text-slate-700 leading-relaxed text-[16px]">
          The TappyUSB Classic SDK only supports legacy TappyUSB devices using the deprecated Classic communication
          protocol. For further information, please see the SDK repository on GitHub.
        </p>
        <div className="mt-5">
          <GitHubBadge href="https://github.com/TapTrack/TappyUSB-CSharp-SDK" />
        </div>
      </section>
    </div>
  );
}

function OverviewTab() {
  return (
    <div>
      <h2 className="text-2xl md:text-3xl font-semibold text-slate-800 mb-4">Overview</h2>
      <p className="text-slate-700 leading-relaxed text-[16px]">
        There are two different Tappy SDKs available for the Windows platform, both written in C#. The TCMP SDK
        supports both TappyBLE and the latest versions of the TappyUSB, while the Classic SDK only supports the
        legacy TappyUSB devices that shipped prior to July 2015. A .NET version of 4.5.2 or greater is required to
        use either SDK. Additionally, the computer using the Tappy will have to have FTDI virtual COM port drivers
        installed. Many machines will already have the necessary drivers, but, if not, they can be downloaded from{" "}
        <a
          href="http://www.ftdichip.com/Drivers/VCP.htm"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#29abe2] hover:underline"
        >
          FTDI
        </a>
        .
      </p>
    </div>
  );
}

function DocsTab() {
  return (
    <div>
      <h2 className="text-2xl md:text-3xl font-semibold text-slate-800 mb-4">Documentation</h2>
      <a
        href="https://docs.google.com/document/d/e/2PACX-1vTnzSyisbi6jD9mepVJsbgf6JL_qWjiIjGGIthRpULt2C5kkpuchm0nYa0U5NSgFOcpzzYzf6S9mGS6/pub?embedded=true"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 text-[#29abe2] hover:underline text-lg font-semibold"
      >
        Read the full documentation here.
        <ExternalLink className="w-4 h-4" />
      </a>
    </div>
  );
}
